import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { PromocodeFacade } from '@features/promocode/facades/promocode.facade';
import { Store } from '@ngrx/store';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { HttpError, LoyaltyProgram } from '@rd/types';
import { PointsTransferMembership } from '@rd/types/membership.type';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import {
  ClearPointsTransferError,
  SubmitPointsTransfer,
} from '../../store/actions';
import { TransferConnectV2State } from '../../store/reducers';
import {
  getCheckoutFormData,
  getCheckoutPointsTransferError,
  getCheckoutPointsTransferLoading,
} from '../../store/selectors/checkout.selectors';
import { EnrichedTransferConnectV2CheckoutFormData } from '../../types';

@Component({
  selector: 'rd-tcv2-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2ReviewComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-tcv2-review') componentClass = true;

  checkoutFormData: EnrichedTransferConnectV2CheckoutFormData;
  checkoutFormData$: Observable<EnrichedTransferConnectV2CheckoutFormData>;
  loyaltyProgram$: Observable<LoyaltyProgram>;
  loyaltyProgramMembership$: Observable<PointsTransferMembership | null>;
  pointsTransferLoading$: Observable<boolean>;

  loyaltyProgramId: string;
  loyaltyProgram: LoyaltyProgram;

  destroy$ = new Subject<boolean>();

  promocode: Promocode | null = null;
  promocodeUsage: PromocodeUsage | null = null;
  promocode$ = this.promocodeFacade.promocode$;
  promocodeUsage$ = this.promocodeFacade.promocodeUsage$;
  promocodeError$ = this.promocodeFacade.promocodeError$;
  promocodeLoading$ = this.promocodeFacade.promocodeLoading$;

  constructor(
    private route: ActivatedRoute,
    private store: Store<{ transferConnectV2: TransferConnectV2State }>,
    private dialogService: DialogService,
    private analyticsService: AnalyticsService,
    private pageTitleService: PageTitleService,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private membershipFacade: MembershipFacade,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
    private promocodeFacade: PromocodeFacade,
  ) {}

  ngOnInit(): void {
    // @ts-ignore
    this.loyaltyProgramId =
      this.route.snapshot.queryParamMap.get('loyaltyProgramId');

    this.assignCheckoutFormData();
    this.assignPromocodeData();
    this.loyaltyProgram$ = this.loyaltyProgramFacade.loyaltyProgramsMap$.pipe(
      map((lpMap) => lpMap[this.loyaltyProgramId]),
    );
    // @ts-ignore
    this.loyaltyProgramMembership$ = this.membershipFacade.memberships$.pipe(
      map((a) => a.find((m) => m.loyaltyProgramId === this.loyaltyProgramId)),
    );
    this.pointsTransferLoading$ = combineLatest([
      this.store.select(getCheckoutPointsTransferLoading),
      // We have to do this because after a create or update on the memberships, we re-fetch them
      this.membershipFacade.loadMembershipsLoading$,
    ]).pipe(
      map((loadingStates) => loadingStates.some((state) => state === true)),
    );
    this.waitForError();
    this.pageTitleService.setPageTitle('transfer-connect-review');
    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.transferConnect.review,
    });
    this.loyaltyProgram$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loyaltyProgram) => {
        this.loyaltyProgram = loyaltyProgram;
      });

    combineLatest([
      this.checkoutFormData$.pipe(
        first((checkoutFormData) => !!checkoutFormData),
      ),
      this.loyaltyProgram$.pipe(first((loyaltyProgram) => !!loyaltyProgram)),
    ]).subscribe(([_, loyaltyProgram]) => {
      this.analyticsService.track({
        // @ts-ignore
        routeName: this.analyticsService.sitemap.transferConnect.reviewFullData,
        extra: { brand: loyaltyProgram.name },
      });
    });
  }

  submitPointsTransfer(): void {
    this.store.dispatch(
      new SubmitPointsTransfer({
        loyaltyProgramId: this.loyaltyProgramId,
        formData: this.checkoutFormData,
        promocode: this.promocode ?? undefined,
        promocodeUsage: this.promocodeUsage ?? undefined,
      }),
    );
  }

  handleApplyPromocode(promocode?: string) {
    if (promocode) {
      this.promocodeFacade.validatePromocode({
        promo_code: {
          code: promocode,
        },
        points_transfer: {
          loyalty_program_id: this.loyaltyProgramId,
          number: this.checkoutFormData.membershipId,
          transfer_amount: this.checkoutFormData.sourceAmount,
        },
      });
    } else {
      this.promocodeFacade.clearPromocodeData();
    }
  }

  ngOnDestroy(): void {
    // Remove promocode since promocode for TC is non-persistent
    // We call this in ngOnDestroy instead of ngOnInit
    // cause there is a case when we redirect from MFA if we remove it in ngOnInit
    // we will lose the promocode status here.
    this.promocodeFacade.clearPromocodeData();

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private assignPromocodeData(): void {
    // @ts-ignore
    this.promocode$.pipe(takeUntil(this.destroy$)).subscribe((promocode) => {
      this.promocode = promocode;
    });

    this.promocodeUsage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((promocodeUsage) => {
        this.promocodeUsage = promocodeUsage;
      });
  }

  private assignCheckoutFormData(): void {
    // @ts-ignore
    this.checkoutFormData$ = combineLatest([
      this.store
        .select(getCheckoutFormData, {
          id: this.loyaltyProgramId,
        })
        .pipe(filter(isNotNull)),
      this.promocodeUsage$,
    ]).pipe(
      map(([{ membershipId, sourceAmount, targetAmount }, promodeUsage]) => {
        const enrichedFormData: EnrichedTransferConnectV2CheckoutFormData = {
          sourceAmount,
          targetAmount,
          membershipId,
          sourceAmountPaid: promodeUsage?.pointsPaid,
        };
        return enrichedFormData;
      }),
    );
    this.checkoutFormData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((checkoutFormData) => {
        this.checkoutFormData = checkoutFormData;
      });
  }

  private waitForError(): void {
    this.store
      .select(getCheckoutPointsTransferError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pointsTransferError) => {
        if (pointsTransferError) {
          this.openErrorDialog(pointsTransferError);
        }
      });
  }

  private openErrorDialog(error: HttpError): void {
    this.dialogService
      .open(CheckoutErrorDialogComponent, {
        data: { error, iconName: 'unexpected-error' },
        id: 'tcv2-review-error-message',
        screenMode: 'center-md',
      })
      .afterClosed.subscribe(() => {
        this.clearPointsTransferError();
      });
  }

  private clearPointsTransferError(): void {
    this.store.dispatch(new ClearPointsTransferError());
  }

  get checkoutPageUrl(): {
    url: string;
    queryParams?: { [key: string]: string };
  } {
    return this.transferConnectV2SettingService.backToCheckoutPageUrl(
      this.loyaltyProgramId,
    );
  }

  get hasPromocode(): boolean {
    return this.transferConnectV2SettingService.hasPromocode;
  }
}
